import React from "react";
import { SectionCard } from "./patientChartShared";

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 100,
  resize: "vertical",
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
};

const toggleWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const toggleButtonStyle = (selected, disabled = false) => ({
  border: selected ? "1px solid #0f172a" : "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: selected ? "#0f172a" : "#fff",
  color: selected ? "#fff" : "#0f172a",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 600,
  opacity: disabled ? 0.6 : 1,
});

const actionButtonStyle = (disabled) => ({
  border: "none",
  borderRadius: 10,
  padding: "10px 14px",
  background: disabled ? "#94a3b8" : "#ea580c",
  color: "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: disabled ? "none" : "0 6px 16px rgba(234, 88, 12, 0.22)",
});

const claimantTypes = ["Spectator", "Volunteer", "Staff", "Vendor", "Player", "Other"];

const causeOptions = [
  { key: "slipFall", label: "Slip/Fall" },
  { key: "tripFall", label: "Trip/Fall" },
  { key: "vehicle", label: "Vehicle" },
  { key: "golfCart", label: "Golf Cart" },
  { key: "struckByBall", label: "Struck By Ball" },
  { key: "foodBeverage", label: "Food/Beverage" },
  { key: "insectBiteSting", label: "Insect Bite/Sting" },
  { key: "sunHeat", label: "Sun/Heat" },
  { key: "struckByAgainst", label: "Struck By/Against" },
];

const conditionOptions = [
  { key: "rain", label: "Rain" },
  { key: "dew", label: "Dew" },
  { key: "dry", label: "Dry" },
  { key: "dark", label: "Dark" },
  { key: "other", label: "Other" },
];

const locationOptions = [
  { key: "hole", label: "Hole" },
  { key: "clubhouse", label: "Clubhouse" },
  { key: "hospitality", label: "Hospitality" },
  { key: "concessionArea", label: "Concession Area" },
  { key: "restroom", label: "Restroom" },
  { key: "parkingLot", label: "Parking Lot" },
  { key: "other", label: "Other" },
];

function formatPhoneInput(value = "") {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function YesNoButtons({ value, onChange, readOnlyMode = false }) {
  return (
    <div style={toggleWrapStyle}>
      <button
        type="button"
        style={toggleButtonStyle(value === true, readOnlyMode)}
        disabled={readOnlyMode}
        onClick={() => {
          if (!readOnlyMode) onChange(true);
        }}
      >
        Yes
      </button>

      <button
        type="button"
        style={toggleButtonStyle(value === false, readOnlyMode)}
        disabled={readOnlyMode}
        onClick={() => {
          if (!readOnlyMode) onChange(false);
        }}
      >
        No
      </button>
    </div>
  );
}

export default function IncidentSection({
  selectedEncounterId,
  incidentForm,
  setIncidentForm,
  medicalForm,
  setMedicalForm,
  encounterForm,
  setEncounterForm,
  expandedSections,
  toggleSection,
  sectionRef,
  activeSection,
  onGenerateIncidentReport,
  isGeneratingIncidentReport = false,
  readOnlyMode = false,
  errorCount = 0,
}) {
  const incident = incidentForm || {};
  const hasEncounter =
    selectedEncounterId !== undefined ? Boolean(selectedEncounterId) : true;

  function setIncidentField(field, value) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => {
      const next = {
        ...prev,
        [field]: field === "witnessPhone" ? formatPhoneInput(value) : value,
      };

      if (field === "description") {
        next.descriptionOfIncident = next.description;
      }

      return next;
    });
  }

  function setNestedField(parent, field, value) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent] || {}),
        [field]: value,
      },
    }));
  }

  function toggleCause(key) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => ({
      ...prev,
      causes: {
        ...(prev.causes || {}),
        [key]: !prev?.causes?.[key],
      },
      playerName:
        key === "struckByBall" && prev?.causes?.[key]
          ? ""
          : prev.playerName || "",
      golfCartNumber:
        key === "golfCart" && prev?.causes?.[key]
          ? ""
          : prev.golfCartNumber || "",
      golfCartDriverName:
        key === "golfCart" && prev?.causes?.[key]
          ? ""
          : prev.golfCartDriverName || "",
      companyName:
        key === "golfCart" && prev?.causes?.[key]
          ? ""
          : prev.companyName || "",
    }));
  }

  function toggleCondition(key) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => ({
      ...prev,
      conditions: {
        ...(prev.conditions || {}),
        [key]: !prev?.conditions?.[key],
      },
    }));
  }

  function toggleLocation(key) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => {
      const nextValue = !prev?.location?.[key];
      const nextLocation = {
        ...(prev.location || {}),
        [key]: nextValue,
      };

      if (key === "hole" && !nextValue) {
        nextLocation.holeNumber = "";
        nextLocation.tee = false;
        nextLocation.fairway = false;
        nextLocation.green = false;
      }

      if (key === "other" && !nextValue) {
        nextLocation.otherText = "";
      }

      return {
        ...prev,
        location: nextLocation,
      };
    });
  }

  function handleClaimantType(type) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => ({
      ...prev,
      claimantType: prev.claimantType === type ? "" : type,
      claimantTypeOther:
        type === "Other" || prev.claimantType !== type
          ? prev.claimantTypeOther || ""
          : "",
      vendorName:
        type === "Vendor" || prev.claimantType !== type
          ? prev.vendorName || ""
          : "",
    }));
  }

  function setWitnessPresent(value) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => ({
      ...prev,
      witnessPresent: value,
      witnessName: value ? prev.witnessName || "" : "",
      witnessAddress: value ? prev.witnessAddress || "" : "",
      witnessPhone: value ? prev.witnessPhone || "" : "",
    }));
  }

  function setPhotosTaken(value) {
    if (readOnlyMode) return;

    setIncidentField("photosTaken", value);
    if (value === false) {
      setIncidentForm((prev) => ({
        ...prev,
        photosTaken: false,
        photos: [],
      }));
    }
  }

  function setFirstAidGiven(value) {
    if (readOnlyMode) return;

    setIncidentField("firstAidGiven", value);
    if (typeof setMedicalForm === "function") {
      setMedicalForm((prev) => ({
        ...(prev || {}),
        firstAidGiven: value,
      }));
    }
  }

  function handlePhotoFilesSelected(event) {
    if (readOnlyMode) return;

    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                name: file.name,
                dataUrl: String(reader.result || ""),
              });
            reader.readAsDataURL(file);
          })
      )
    ).then((items) => {
      if (readOnlyMode) return;

      setIncidentForm((prev) => ({
        ...prev,
        photosTaken: true,
        photos: [...(prev.photos || []), ...items],
      }));
    });

    event.target.value = "";
  }

  function removePhoto(index) {
    if (readOnlyMode) return;

    setIncidentForm((prev) => {
      const nextPhotos = (prev.photos || []).filter((_, i) => i !== index);
      return {
        ...prev,
        photos: nextPhotos,
        photosTaken: nextPhotos.length > 0 ? true : prev.photosTaken,
      };
    });
  }

  const showVendorName = incident.claimantType === "Vendor";
  const showClaimantOther = incident.claimantType === "Other";
  const showGolfCartFields = Boolean(incident?.causes?.golfCart);
  const showPlayerName = Boolean(incident?.causes?.struckByBall);
  const showHoleFields = Boolean(incident?.location?.hole);
  const showLocationOther = Boolean(incident?.location?.other);
  const showConditionOther = Boolean(incident?.conditions?.other);
  const showWitnessFields = incident.witnessPresent === true;

  const canGenerate =
    hasEncounter &&
    !readOnlyMode &&
    typeof onGenerateIncidentReport === "function";

  return (
    <SectionCard
      title="Incident Report Section"
      errorCount={errorCount}
      sectionKey="incident"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={sectionRef}
      activeSection={activeSection}
      extraHeaderContent={
        hasEncounter && typeof onGenerateIncidentReport === "function" ? (
          <button
            type="button"
            onClick={onGenerateIncidentReport}
            disabled={isGeneratingIncidentReport || readOnlyMode}
            style={actionButtonStyle(isGeneratingIncidentReport || readOnlyMode)}
          >
            {isGeneratingIncidentReport
              ? "Generating..."
              : "Generate Incident Report"}
          </button>
        ) : null
      }
    >
      {!hasEncounter ? (
        <div style={{ color: "#64748b" }}>
          Create or select an encounter first.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 18 }}>
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Claimant Type</div>
            <div style={toggleWrapStyle}>
              {claimantTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  style={toggleButtonStyle(incident.claimantType === type, readOnlyMode)}
                  disabled={readOnlyMode}
                  onClick={() => handleClaimantType(type)}
                >
                  {type}
                </button>
              ))}
            </div>

            {showVendorName && (
              <label style={labelStyle}>
                Vendor Name
                <input
                  style={inputStyle}
                  value={incident.vendorName || ""}
                  onChange={(e) => setIncidentField("vendorName", e.target.value)}
                  disabled={readOnlyMode}
                />
              </label>
            )}

            {showClaimantOther && (
              <label style={labelStyle}>
                Other Claimant Type
                <input
                  style={inputStyle}
                  value={incident.claimantTypeOther || ""}
                  onChange={(e) =>
                    setIncidentField("claimantTypeOther", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>
            )}
          </div>

          <label style={labelStyle}>
            Description of Incident
            <textarea
              style={textareaStyle}
              placeholder="Describe what happened."
              value={incident.description || ""}
              onChange={(e) => setIncidentField("description", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              Conditions at Time of Accident
            </div>
            <div style={toggleWrapStyle}>
              {conditionOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  style={toggleButtonStyle(
                    Boolean(incident?.conditions?.[option.key]),
                    readOnlyMode
                  )}
                  disabled={readOnlyMode}
                  onClick={() => toggleCondition(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {showConditionOther && (
              <label style={labelStyle}>
                Other Condition
                <input
                  style={inputStyle}
                  value={incident?.conditions?.otherText || ""}
                  onChange={(e) =>
                    setNestedField("conditions", "otherText", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>
            )}
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              Cause of Incident
            </div>
            <div style={toggleWrapStyle}>
              {causeOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  style={toggleButtonStyle(
                    Boolean(incident?.causes?.[option.key]),
                    readOnlyMode
                  )}
                  disabled={readOnlyMode}
                  onClick={() => toggleCause(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {showGolfCartFields && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                <label style={labelStyle}>
                  Golf Cart Number
                  <input
                    style={inputStyle}
                    value={incident.golfCartNumber || ""}
                    onChange={(e) =>
                      setIncidentField("golfCartNumber", e.target.value)
                    }
                    disabled={readOnlyMode}
                  />
                </label>

                <label style={labelStyle}>
                  Golf Cart Driver Name
                  <input
                    style={inputStyle}
                    value={incident.golfCartDriverName || ""}
                    onChange={(e) =>
                      setIncidentField("golfCartDriverName", e.target.value)
                    }
                    disabled={readOnlyMode}
                  />
                </label>

                <label style={labelStyle}>
                  Company Name
                  <input
                    style={inputStyle}
                    value={incident.companyName || ""}
                    onChange={(e) =>
                      setIncidentField("companyName", e.target.value)
                    }
                    disabled={readOnlyMode}
                  />
                </label>
              </div>
            )}

            {showPlayerName && (
              <label style={labelStyle}>
                Player Name
                <input
                  style={inputStyle}
                  value={incident.playerName || ""}
                  onChange={(e) => setIncidentField("playerName", e.target.value)}
                  disabled={readOnlyMode}
                />
              </label>
            )}
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              Location of Incident
            </div>
            <div style={toggleWrapStyle}>
              {locationOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  style={toggleButtonStyle(
                    Boolean(incident?.location?.[option.key]),
                    readOnlyMode
                  )}
                  disabled={readOnlyMode}
                  onClick={() => toggleLocation(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {showHoleFields && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={labelStyle}>
                  Hole Number
                  <input
                    style={inputStyle}
                    value={incident?.location?.holeNumber || ""}
                    onChange={(e) =>
                      setNestedField("location", "holeNumber", e.target.value)
                    }
                    disabled={readOnlyMode}
                  />
                </label>

                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ fontWeight: 600, color: "#334155" }}>
                    Hole Area
                  </div>
                  <div style={toggleWrapStyle}>
                    {["tee", "fairway", "green"].map((key) => (
                      <button
                        key={key}
                        type="button"
                        style={toggleButtonStyle(
                          Boolean(incident?.location?.[key]),
                          readOnlyMode
                        )}
                        disabled={readOnlyMode}
                        onClick={() =>
                          setNestedField(
                            "location",
                            key,
                            !incident?.location?.[key]
                          )
                        }
                      >
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {showLocationOther && (
              <label style={labelStyle}>
                Other Location Description
                <input
                  style={inputStyle}
                  value={incident?.location?.otherText || ""}
                  onChange={(e) =>
                    setNestedField("location", "otherText", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 16,
            }}
          >
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                Witness Present?
              </div>
              <YesNoButtons
                value={incident.witnessPresent}
                onChange={setWitnessPresent}
                readOnlyMode={readOnlyMode}
              />
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                Photos Taken?
              </div>
              <YesNoButtons
                value={incident.photosTaken}
                onChange={setPhotosTaken}
                readOnlyMode={readOnlyMode}
              />
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                First Aid Given?
              </div>
              <YesNoButtons
                value={incident.firstAidGiven}
                onChange={setFirstAidGiven}
                readOnlyMode={readOnlyMode}
              />
            </div>
          </div>

          {incident.photosTaken && (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Photos</div>

              <label style={labelStyle}>
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  Upload / Take Photo
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handlePhotoFilesSelected}
                  disabled={readOnlyMode}
                />
              </label>

              {!!incident?.photos?.length && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12,
                  }}
                >
                  {incident.photos.map((photo, index) => (
                    <div
                      key={`${photo.name || "photo"}-${index}`}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: 8,
                        display: "grid",
                        gap: 8,
                        background: "#fff",
                      }}
                    >
                      <img
                        src={photo.dataUrl}
                        alt={photo.name || `Photo ${index + 1}`}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e2e8f0",
                        }}
                      />
                      <div
                        style={{
                          fontSize: 12,
                          color: "#475569",
                          wordBreak: "break-word",
                        }}
                      >
                        {photo.name || `Photo ${index + 1}`}
                      </div>
                      <button
                        type="button"
                        style={toggleButtonStyle(false, readOnlyMode)}
                        disabled={readOnlyMode}
                        onClick={() => removePhoto(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {showWitnessFields && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label style={labelStyle}>
                Witness Name
                <input
                  style={inputStyle}
                  value={incident.witnessName || ""}
                  onChange={(e) =>
                    setIncidentField("witnessName", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>

              <label style={labelStyle}>
                Witness Address
                <input
                  style={inputStyle}
                  value={incident.witnessAddress || ""}
                  onChange={(e) =>
                    setIncidentField("witnessAddress", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>

              <label style={labelStyle}>
                Witness Phone
                <input
                  style={inputStyle}
                  value={incident.witnessPhone || ""}
                  onChange={(e) =>
                    setIncidentField("witnessPhone", e.target.value)
                  }
                  disabled={readOnlyMode}
                />
              </label>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              Other Information
            </div>

            <div style={toggleWrapStyle}>
              <button
                type="button"
                style={toggleButtonStyle(
                  Boolean(incident.otherInformationNA),
                  readOnlyMode
                )}
                disabled={readOnlyMode}
                onClick={() => {
                  if (readOnlyMode) return;

                  setIncidentForm((prev) => ({
                    ...prev,
                    otherInformationNA: !prev.otherInformationNA,
                    otherInformation: !prev.otherInformationNA
                      ? ""
                      : prev.otherInformation || "",
                  }));
                }}
              >
                N/A
              </button>
            </div>

            {!incident.otherInformationNA && (
              <textarea
                style={textareaStyle}
                value={incident.otherInformation || ""}
                onChange={(e) =>
                  setIncidentField("otherInformation", e.target.value)
                }
                disabled={readOnlyMode}
              />
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}